import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    Coins, Gift, ShoppingCart, History, TrendingUp, 
    Store, ArrowRight, Loader2, Info, QrCode
} from 'lucide-react';
import { apiRequest, formatPrice, formatDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function LoyaltyPage() {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [pointsData, setPointsData] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [qrToken, setQrToken] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate('/auth');
            return;
        }
        loadLoyaltyData();
    }, [user, authLoading]);

    const loadLoyaltyData = async () => {
        setLoading(true);
        try {
            const [points, historyData, qr] = await Promise.all([
                apiRequest('/loyalty/points'),
                apiRequest('/loyalty/history'),
                apiRequest('/loyalty/qr-token').catch(() => null),
            ]);
            setPointsData(points);
            setHistory(historyData);
            if (qr?.token) setQrToken(qr.token);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen pt-20 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="loyalty-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Gift className="h-10 w-10 text-amber-500" />
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">Programme Fidélité</h1>
                    </div>
                    <p className="text-muted-foreground text-lg">
                        Cumulez des points et économisez sur vos achats !
                    </p>
                </div>

                {/* Points Balance Card */}
                <Card className="glass rounded-3xl mb-8 overflow-hidden" data-testid="points-balance">
                    <div className="bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-amber-500/5 p-8">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                            <div className="text-center sm:text-left">
                                <p className="text-muted-foreground mb-2">Votre solde actuel</p>
                                <div className="flex items-center gap-3">
                                    <Coins className="h-12 w-12 text-amber-500" />
                                    <div>
                                        <p className="font-heading text-5xl font-bold text-amber-500">
                                            {pointsData?.points || 0}
                                        </p>
                                        <p className="text-lg text-muted-foreground">points</p>
                                    </div>
                                </div>
                            </div>
                            <div className="text-center sm:text-right">
                                <p className="text-muted-foreground mb-2">Valeur en euros</p>
                                <p className="font-heading text-4xl font-bold text-foreground">
                                    {pointsData?.value_euros || 0}€
                                </p>
                                <p className="text-sm text-muted-foreground">de réduction disponible</p>
                            </div>
                        </div>
                    </div>
                    <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button 
                                className="flex-1 rounded-full" 
                                onClick={() => navigate('/shop')}
                                data-testid="earn-more-btn"
                            >
                                <Store className="h-4 w-4 mr-2" />
                                Gagner plus de points
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                            <Button 
                                variant="secondary" 
                                className="flex-1 rounded-full"
                                disabled={!pointsData?.points}
                            >
                                <ShoppingCart className="h-4 w-4 mr-2" />
                                Utiliser en magasin
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* QR Code for in-store redemption */}
                {qrToken && (
                    <Card className="glass rounded-3xl mb-8 overflow-hidden" data-testid="loyalty-qr-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <QrCode className="h-5 w-5 text-primary" />
                                Votre code fidélité magasin
                            </CardTitle>
                            <CardDescription>
                                Présentez ce code au comptoir à Flayosc — le vendeur le scanne pour utiliser vos points.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col sm:flex-row items-center gap-6">
                                <div className="bg-white p-4 rounded-2xl shadow-inner border-2 border-amber-200">
                                    <QRCodeCanvas
                                        value={`${window.location.origin}/admin/loyalty/scan/${qrToken}`}
                                        size={180}
                                        level="M"
                                        includeMargin={false}
                                        data-testid="loyalty-qr-canvas"
                                    />
                                </div>
                                <div className="flex-1 text-center sm:text-left space-y-2">
                                    <p className="text-sm text-muted-foreground">
                                        <strong>Pour le client :</strong> ouvrez cette page sur votre téléphone et présentez le QR au vendeur.
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        <strong>Pour le vendeur :</strong> scannez le code, il pré-remplit l'écran "Retirer des points" dans l'admin.
                                    </p>
                                    <div className="pt-2">
                                        <code className="text-xs bg-muted px-2 py-1 rounded">{qrToken}</code>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* How it works */}
                <Card className="glass rounded-3xl mb-8">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Info className="h-5 w-5 text-primary" />
                            Comment ça marche ?
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid sm:grid-cols-3 gap-6">
                            <div className="text-center p-4 rounded-2xl bg-muted/50">
                                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                                    <ShoppingCart className="h-6 w-6 text-primary" />
                                </div>
                                <h4 className="font-semibold mb-1">Achetez</h4>
                                <p className="text-sm text-muted-foreground">
                                    Faites vos achats dans notre boutique
                                </p>
                            </div>
                            <div className="text-center p-4 rounded-2xl bg-muted/50">
                                <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-3">
                                    <TrendingUp className="h-6 w-6 text-amber-500" />
                                </div>
                                <h4 className="font-semibold mb-1">Cumulez</h4>
                                <p className="text-sm text-muted-foreground">
                                    <strong>1 point</strong> gagné tous les <strong>10€</strong> d&apos;achat
                                </p>
                            </div>
                            <div className="text-center p-4 rounded-2xl bg-muted/50">
                                <div className="h-12 w-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
                                    <Gift className="h-6 w-6 text-emerald-500" />
                                </div>
                                <h4 className="font-semibold mb-1">Économisez</h4>
                                <p className="text-sm text-muted-foreground">
                                    <strong>1 point = 1€</strong> de réduction en magasin
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* History */}
                <Card className="glass rounded-3xl">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <History className="h-5 w-5 text-primary" />
                            Historique des points
                        </CardTitle>
                        <CardDescription>
                            Vos gains de points sur les dernières commandes
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {history.length === 0 ? (
                            <div className="text-center py-12">
                                <Coins className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                                <p className="text-muted-foreground">Aucun historique de points</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Faites votre premier achat pour commencer à cumuler !
                                </p>
                                <Button 
                                    className="mt-4 rounded-full" 
                                    onClick={() => navigate('/shop')}
                                >
                                    <Store className="h-4 w-4 mr-2" />
                                    Voir la boutique
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {history.map((item, index) => (
                                    <div 
                                        key={index} 
                                        className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                                        data-testid={`history-item-${index}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                                item.type === 'earned' 
                                                    ? 'bg-emerald-500/10' 
                                                    : 'bg-red-500/10'
                                            }`}>
                                                {item.type === 'earned' ? (
                                                    <TrendingUp className="h-5 w-5 text-emerald-500" />
                                                ) : (
                                                    <Gift className="h-5 w-5 text-red-500" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-medium">
                                                    {item.type === 'earned' ? 'Points gagnés' : 'Points utilisés'}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    Commande {formatPrice(item.amount)} • {formatDate(item.date)}
                                                </p>
                                            </div>
                                        </div>
                                        <Badge className={`${
                                            item.type === 'earned'
                                                ? 'bg-emerald-500/10 text-emerald-500'
                                                : 'bg-red-500/10 text-red-500'
                                        } border-0 font-bold`}>
                                            {item.type === 'earned' ? '+' : '-'}{item.points_earned} pts
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Info banner */}
                <div className="mt-8 p-4 rounded-2xl bg-primary/5 border border-primary/20">
                    <p className="text-sm text-center text-muted-foreground">
                        <strong>Note :</strong> Les points sont utilisables en magasin à Flayosc. 
                        Présentez votre compte à la caisse pour bénéficier de vos réductions.
                    </p>
                </div>
            </div>
        </div>
    );
}
